import React, { Component, createRef } from "react";
import { Box, Typography } from "@mui/joy";
import { WebRTCContext } from "../../../contexts/WebRTCContext";
import PlayerControls from "./PlayerControls";

// Format time to HH:MM:SS
function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

class VideoPlayer extends Component {

    static FAST_SEEK_MS = 5000;

    static contextType = WebRTCContext;

    constructor(props) {
        super(props);
        this.videoRef = createRef();
        this.state = {
            isLive: false,
            isPlaying: false,
            currentDeviceId: null,
            absoluteStartMs: 0,
            absoluteEndMs: 0,
            absolutePositionMs: 0,
            recordingId: null,
        };
    }

    componentDidMount() {
        this.initDataFlows();
    }

    componentWillUnmount() {
        // TODO implement some cleanup
    }

    initDataFlows() {
        const { rtcSession } = this.context;
        console.log('Trying to assign remote stream to video element');

        if (rtcSession && rtcSession.remoteStream && this.videoRef.current) {
            this.videoRef.current.srcObject = rtcSession.remoteStream;
            console.log('Remote stream assigned.');

            rtcSession.datachannelMsgHandler?.setMsgListener({
                onmessage: (message) => {
                    console.log("DataChannel Message:", message);
                    if (message.command === "playback_progress_update") {
                        this.updatePlaybackProgress(
                            message.device_id,
                            message.recording_id,
                            message.start_ms,
                            message.end_ms,
                            message.current_ms
                        );
                    }
                },
                onopen: () => {
                    console.log("DataChannel Opened");
                },
                onclose: () => {
                    console.log("DataChannel Closed");
                },
            });
        } else {
            console.log("No remote stream found.");
        }
    }

    handleSeekStart = () => {
        console.log("handleSeekStart");
        this.sendPauseCommand();
    };

    handleSeeking = (percentage) => {
        console.log("handleSeeking ", percentage );
        const newUtcTime = this.state.absoluteStartMs
            +  (this.state.absoluteEndMs - this.state.absoluteStartMs) * (percentage / 100);
        this.setState({ absolutePositionMs: newUtcTime });
    };

    handleSeekEnd = (percentage) => {
        console.log("handleSeekEnd ", percentage );
        const newUtcTime = this.state.absoluteStartMs
            +  (this.state.absoluteEndMs - this.state.absoluteStartMs) * (percentage / 100);
        this.sendSeekCommand(this.state.currentDeviceId, newUtcTime, true);
    };

    handlePlayPause = () => {
        if (this.state.isPlaying) {
            this.sendPauseCommand();
        } else {
            const newUtcTime = this.state.absolutePositionMs
            this.sendSeekCommand(this.state.currentDeviceId, newUtcTime, true);
        }
    };

    playRecording(deviceId, recording) {
        const { rtcSession } = this.context;
        if (deviceId && rtcSession?.commandsDataChannel) {
            this.currentDeviceId = deviceId;
            this.recordingId = recording.id;
            let playRecCommand = JSON.stringify({
                command: "play",
                device_id: deviceId,
                position: recording.start_ms,
            });
            rtcSession.commandsDataChannel.send(playRecCommand);
            this.setState({isLive: false});
        } else {
            console.error(`Unable to play recording for device: ${deviceId}`);
        }
    }

    updatePlaybackProgress(deviceId, recordingId, startMs, endMs, currentMs) {
        if (this.state.recordingId && this.state.recordingId !== recordingId) {
            // with continuous playback we need to notify host about track switch by media server
            if (this.props.onRecordingSwitched) {
                this.props.onRecordingSwitched(recordingId);
            }
        }
        this.setState({
            currentDeviceId: deviceId,
            recordingId: recordingId,
            absoluteStartMs: startMs,
            absoluteEndMs: endMs,
            absolutePositionMs: currentMs,
            isPlaying: true
        });
    }

    showLiveView(deviceId) {
        const { rtcSession } = this.context;
        if (deviceId && rtcSession?.commandsDataChannel) {
            this.currentDeviceId = deviceId;
            this.recordingId = null;
            let liveCommand = JSON.stringify({
                command: "live",
                device_id: deviceId,
            });
            rtcSession.commandsDataChannel.send(liveCommand);
            this.setState({
                isLive: true,
                isPlaying: false
            });
        } else {
            console.error(`Unable to start live view for device: ${deviceId}`);
        }
    }

    sendPauseCommand() {
        const { rtcSession } = this.context;
        if (rtcSession?.commandsDataChannel) {
            let pauseCommand = JSON.stringify({ command: "pause" });
            rtcSession.commandsDataChannel.send(pauseCommand);
            this.setState({
                isLive: false,
                isPlaying: false
            });
        } else {
            console.error("DataChannel is not available for sending pause commands.");
        }
    }

    // Send a SEEK command to the media server
    sendSeekCommand(deviceId, positionAbsoluteMs, start_playback) {
        const { rtcSession } = this.context;
        if (deviceId) {
            let followupAction;
            if (start_playback === true) {
                followupAction = "play"
            } else {
                followupAction = "none"
            }
            let seekCommand = `{
                                    "command": "seek",
                                    "device_id": ${deviceId},
                                    "position": ${positionAbsoluteMs},
                                    "follow_action": "${followupAction}"
                                }`;
            if (rtcSession?.commandsDataChannel) {
                rtcSession.commandsDataChannel.send(seekCommand);
                console.log('Seek command sent:', seekCommand);
            } else {
                console.error('DataChannel is not available for sending seek commands.');
            }
        } else {
            console.error(`Unable to find a corresponding recording to seek`);
        }
    }

    getProgressPercentage = () => {
        if ((this.state.absoluteEndMs - this.state.absoluteStartMs) === 0) return 0;
        return (this.state.absolutePositionMs - this.state.absoluteStartMs)
            / (this.state.absoluteEndMs - this.state.absoluteStartMs) * 100
    };

    handleFullscreenToggle = () => {
        if (!document.fullscreenElement) {
            if (this.videoRef.current.requestFullscreen) {
                this.videoRef.current.requestFullscreen();
            } else if (this.videoRef.current.mozRequestFullScreen) { // Firefox
                this.videoRef.current.mozRequestFullScreen();
            } else if (this.videoRef.current.webkitRequestFullscreen) { // Chrome, Safari, Opera
                this.videoRef.current.webkitRequestFullscreen();
            } else if (this.videoRef.current.msRequestFullscreen) { // IE/Edge
                this.videoRef.current.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.mozCancelFullScreen) { // Firefox
                document.mozCancelFullScreen();
            } else if (document.webkitExitFullscreen) { // Chrome, Safari, Opera
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) { // IE/Edge
                document.msExitFullscreen();
            }
        }
    };

    handleNext = () => {
        console.log('handleNext');
        if (this.props.onNext) {
            this.props.onNext();
        }
    }

    handlePrevious = () => {
        console.log('handlePrevious');
        if (this.props.onPrevious) {
            this.props.onPrevious();
        }
    }

    handleRewind = () => {
        console.log('handleRewind');
        if (this.state.absolutePositionMs) {
            let newPositionMs = this.state.absolutePositionMs - VideoPlayer.FAST_SEEK_MS;
            if (newPositionMs < this.state.absoluteStartMs) {
                newPositionMs = this.state.absoluteStartMs;
            }
            this.sendSeekCommand(this.state.currentDeviceId, newPositionMs, true)
        }
    }

    handleForward = () => {
        console.log('handleForward');
        if (this.state.absolutePositionMs) {
            let newPositionMs = this.state.absolutePositionMs + VideoPlayer.FAST_SEEK_MS;
            if (newPositionMs > this.state.absoluteEndMs) {
                newPositionMs = this.state.absoluteEndMs;
            }
            this.sendSeekCommand(this.state.currentDeviceId, newPositionMs, true)
        }
    }

    handleVideoClick = (event) => {
        if (!this.videoRef.current) return;

        const rect = this.videoRef.current.getBoundingClientRect();
        // Calculate click position as percentages
        const xPercent = Math.floor(((event.clientX - rect.left) / rect.width) * 100);
        const yPercent = Math.floor(((event.clientY - rect.top) / rect.height) * 100);
        console.log(`Click coordinates: ${xPercent}% X, ${yPercent}% Y`);
        this.sendClickCoordinates(xPercent, yPercent)
    };

    sendClickCoordinates = (xPercent, yPercent) => {
        const { rtcSession } = this.context;
        let coordSelectionCmd = `{
                                    "command": "select",
                                    "x_coord": ${xPercent},
                                    "y_coord": ${yPercent}
                                }`;
        if (rtcSession?.commandsDataChannel) {
            rtcSession.commandsDataChannel.send(coordSelectionCmd);
            console.log('coordSelectionCmd: ', coordSelectionCmd);
        } else {
            console.error('DataChannel is not available for sending coordinates command.');
        }
    }


    render() {
        const { absoluteStartMs, absoluteEndMs, absolutePositionMs, isLive } = this.state;

        return (
            <Box sx={{ width: "100%", position: "relative", mx: "auto" }}>
                <video
                    ref={this.videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{ width: "100%", display: "block" }}
                    onClick={this.handleVideoClick}
                />

                {/* Show controls only when NOT in live mode */}
                {!isLive && (
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <PlayerControls
                        sx={{ px: 2, flexGrow: 1 }}
                        progress={this.getProgressPercentage()}
                        progressSecLabel={absolutePositionMs ? formatTime((absolutePositionMs - absoluteStartMs) / 1000) : "-"}
                        durationSecLabel={absoluteStartMs ? formatTime((absoluteEndMs - absoluteStartMs) / 1000) : "-"}
                        isPlaying={this.state.isPlaying}
                        isMuted={true}
                        onSeekStart={this.handleSeekStart}
                        onSeeking={this.handleSeeking}
                        onSeekEnd={this.handleSeekEnd}
                        onPlayPause={this.handlePlayPause}
                        onFullscreen={this.handleFullscreenToggle}
                        onNext={this.handleNext}
                        onPrevious={this.handlePrevious}
                        onRewind={this.handleRewind}
                        onForward={this.handleForward}
                    />
                </Box>
                )}
                {isLive && (
                    <Typography
                        sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            backgroundColor: "rgba(255, 0, 0, 0.7)", // Semi-transparent red
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: 14,
                            padding: "5px 10px",
                            borderRadius: "5px",
                        }}
                    >
                        Live
                    </Typography>
                )}
            </Box>
        );
    }
}

export default VideoPlayer;
